import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import CustomSelect from '../ui/CustomSelect'; // Assuming CustomSelect is suitable for dropdowns
import { createBotVersion, fetchStrategiesMetadata } from '../../api/botsApi';
import { useIndicatorMetadata } from '../../context/IndicatorMetadataContext';

const BotVersionForm = ({ onSuccess }) => {
  const navigate = useNavigate();
  const { botId } = useParams();
  const { indicatorMetadata, isLoading: isLoadingMetadata, error: metadataError } = useIndicatorMetadata();

  const [strategiesMetadata, setStrategiesMetadata] = useState([]);
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [strategyParams, setStrategyParams] = useState({});
  const [indicatorConfigs, setIndicatorConfigs] = useState([]);
  const [notes, setNotes] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMetadata = async () => {
      setError(null);
      try {
        const strategies = await fetchStrategiesMetadata();
        setStrategiesMetadata(strategies);
      } catch (err) {
        console.error("Failed to fetch strategies metadata:", err);
        setError(err.message || "Failed to load strategies metadata.");
      }
    };
    fetchMetadata();
  }, []);

  const initializeStrategyAndIndicators = useCallback((strategy) => {
    if (!strategy) {
      setSelectedStrategy(null);
      setStrategyParams({});
      setIndicatorConfigs([]);
      return;
    }

    setSelectedStrategy(strategy);

    // Initialize strategy parameters with default values
    const initialStrategyParams = {};
    strategy.parameters.forEach(param => {
      initialStrategyParams[param.name] = param.default_value;
    });
    setStrategyParams(initialStrategyParams);

    // Initialize required indicators
    const initialIndicatorConfigs = strategy.required_indicators.map(reqInd => {
      const indicatorMeta = indicatorMetadata.find(ind => ind.name === reqInd.name);
      if (!indicatorMeta) {
        console.warn(`Metadata for required indicator ${reqInd.name} not found.`);
        return null;
      }

      const params = {};
      indicatorMeta.parameters.forEach(indParam => {
        // Check if the indicator parameter is linked to a strategy parameter
        if (reqInd.params && reqInd.params[indParam.name]) {
          // The indicator's parameter (indParam.name) is linked to a strategy parameter (reqInd.params[indParam.name])
          params[indParam.name] = initialStrategyParams[reqInd.params[indParam.name]];
        } else {
          params[indParam.name] = indParam.default_value;
        }
      });
      return { name: reqInd.name, params };
    }).filter(Boolean); // Remove nulls if metadata not found
    setIndicatorConfigs(initialIndicatorConfigs);
  }, [indicatorMetadata]);

  // Effect to re-initialize indicators when strategyParams change (for linked parameters)
  useEffect(() => {
    if (selectedStrategy && indicatorMetadata.length > 0) {
      const updatedIndicatorConfigs = selectedStrategy.required_indicators.map(reqInd => {
        const indicatorMeta = indicatorMetadata.find(ind => ind.name === reqInd.name);
        if (!indicatorMeta) return null;

        const params = {};
        indicatorMeta.parameters.forEach(indParam => {
          // Correctly find the linked strategy parameter name
          const linkedStrategyParamName = reqInd.params[indParam.name];
          if (linkedStrategyParamName && strategyParams.hasOwnProperty(linkedStrategyParamName)) {
            params[indParam.name] = strategyParams[linkedStrategyParamName];
          } else {
            // Fallback to the indicator's default value if not linked or strategy param not set
            params[indParam.name] = indParam.default_value;
          }
        });
        return { name: reqInd.name, params };
      }).filter(Boolean);
      setIndicatorConfigs(updatedIndicatorConfigs);
    }
  }, [strategyParams, selectedStrategy, indicatorMetadata]);


  const handleStrategyChange = (strategyName) => {
    const strategy = strategiesMetadata.find(s => s.name === strategyName);
    initializeStrategyAndIndicators(strategy);
  };

  const handleStrategyParamChange = (paramName, value, type) => {
    let processedValue = value;
    if (type === 'int') {
      processedValue = parseInt(value, 10);
    } else if (type === 'float') {
      processedValue = parseFloat(value);
    } else if (type === 'bool') {
      processedValue = value === 'true';
    }
    setStrategyParams(prev => ({ ...prev, [paramName]: processedValue }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    if (!selectedStrategy) {
      setError("Please select a strategy.");
      setIsLoading(false);
      return;
    }

    // Validate strategy parameters
    const validatedStrategyParams = {};
    for (const paramDef of selectedStrategy.parameters) {
      let value = strategyParams[paramDef.name];
      // Basic validation and type conversion
      if (paramDef.parameter_type === 'int') {
        value = parseInt(value, 10);
        if (isNaN(value) || (paramDef.min_value !== null && value < paramDef.min_value) || (paramDef.max_value !== null && value > paramDef.max_value)) {
          setError(`Invalid value for ${paramDef.display_name}. Must be an integer between ${paramDef.min_value} and ${paramDef.max_value}.`);
          setIsLoading(false);
          return;
        }
      } else if (paramDef.parameter_type === 'float') {
        value = parseFloat(value);
        if (isNaN(value) || (paramDef.min_value !== null && value < paramDef.min_value) || (paramDef.max_value !== null && value > paramDef.max_value)) {
          setError(`Invalid value for ${paramDef.display_name}. Must be a number between ${paramDef.min_value} and ${paramDef.max_value}.`);
          setIsLoading(false);
          return;
        }
      } else if (paramDef.parameter_type === 'enum' && paramDef.options && !paramDef.options.includes(value)) {
        setError(`Invalid value for ${paramDef.display_name}. Must be one of: ${paramDef.options.join(', ')}.`);
        setIsLoading(false);
        return;
      }
      validatedStrategyParams[paramDef.name] = value;
    }

    // Prepare indicator configs for submission
    const finalIndicatorConfigs = indicatorConfigs.map(indConfig => {
      const indicatorMeta = indicatorMetadata.find(ind => ind.name === indConfig.name);
      if (!indicatorMeta) return null; // Should not happen if initialized correctly

      const params = {};
      indicatorMeta.parameters.forEach(indParamDef => {
        let value = indConfig.params[indParamDef.name];
        // Apply type conversion for indicator parameters
        if (indParamDef.parameter_type === 'int') {
          value = parseInt(value, 10);
        } else if (indParamDef.parameter_type === 'float') {
          value = parseFloat(value);
        } else if (indParamDef.parameter_type === 'bool') {
          value = value === 'true';
        }
        params[indParamDef.name] = value;
      });
      return { name: indConfig.name, params };
    }).filter(Boolean);

    const versionData = {
      bot_id: botId,
      strategy_name: selectedStrategy.name,
      strategy_params: validatedStrategyParams,
      indicator_configs: finalIndicatorConfigs,
      notes,
    };

    try {
      await createBotVersion(versionData);
      if (onSuccess) {
        onSuccess();
      } else {
        navigate(`/bots/${botId}`);
      }
    } catch (err) {
      console.error("Failed to create bot version:", err);
      setError(err.message || `Failed to create bot version. ${err.detail || ''}`);
    } finally {
      setIsLoading(false);
    }
  };

  const renderParameterInput = (paramDef, value, onChangeHandler) => {
    const commonProps = {
      id: paramDef.name,
      name: paramDef.name,
      value: value || '',
      onChange: (e) => onChangeHandler(paramDef.name, e.target.value, paramDef.parameter_type),
      className: "mt-1 w-full",
      placeholder: `Default: ${paramDef.default_value}`,
      required: true, // Assuming all defined params are required
    };

    switch (paramDef.parameter_type) {
      case 'int':
      case 'float':
        return (
          <Input
            type="number"
            step={paramDef.parameter_type === 'float' ? 'any' : paramDef.step || 1}
            min={paramDef.min_value || undefined}
            max={paramDef.max_value || undefined}
            {...commonProps}
          />
        );
      case 'bool':
        return (
          <CustomSelect
            options={[{ value: 'true', label: 'True' }, { value: 'false', label: 'False' }]}
            value={String(value)}
            onChange={(val) => onChangeHandler(paramDef.name, val, paramDef.parameter_type)}
            placeholder="Select boolean"
            className="mt-1 w-full"
          />
        );
      case 'enum':
        return (
          <CustomSelect
            options={paramDef.options.map(opt => ({ value: opt, label: opt }))}
            value={value}
            onChange={(val) => onChangeHandler(paramDef.name, val, paramDef.parameter_type)}
            placeholder={`Select ${paramDef.display_name}`}
            className="mt-1 w-full"
          />
        );
      case 'str':
      default:
        return <Input type="text" {...commonProps} />;
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 p-4 bg-backgroundOffset rounded-lg shadow-md">
      {error && <p className="text-destructive text-center mb-4">{error}</p>}

      {isLoadingMetadata ? (
        <p className="text-textSecondary text-center">Loading strategies and indicators...</p>
      ) : (
        <>
          {/* Strategy Selection */}
          <div className="space-y-2">
            <label htmlFor="strategy-select" className="block text-sm font-medium text-textPrimary">
              Select Strategy
            </label>
            <CustomSelect
              id="strategy-select"
              options={strategiesMetadata.map(s => ({ value: s.name, label: s.display_name }))}
              value={selectedStrategy ? selectedStrategy.name : ''}
              onChange={handleStrategyChange}
              placeholder="Choose a strategy"
              className="w-full"
            />
            {selectedStrategy && (
              <p className="text-xs text-textSecondary mt-1">{selectedStrategy.description}</p>
            )}
          </div>

          {/* Strategy Parameters */}
          {selectedStrategy && (
            <div className="space-y-4 border border-borderDefault rounded-md p-4 bg-backgroundSurface">
              <h3 className="text-lg font-semibold text-textPrimary">Strategy Parameters</h3>
              {selectedStrategy.parameters.length === 0 ? (
                <p className="text-sm text-textSecondary">No configurable parameters for this strategy.</p>
              ) : (
                selectedStrategy.parameters.map(paramDef => (
                  <div key={paramDef.name} className="space-y-1">
                    <label htmlFor={paramDef.name} className="block text-sm font-medium text-textPrimary">
                      {paramDef.display_name}
                    </label>
                    {renderParameterInput(paramDef, strategyParams[paramDef.name], handleStrategyParamChange)}
                    {paramDef.description && (
                      <p className="text-xs text-textSecondary">{paramDef.description}</p>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {/* Required Indicators */}
          {selectedStrategy && indicatorConfigs.length > 0 && (
            <div className="space-y-4 border border-borderDefault rounded-md p-4 bg-backgroundSurface">
              <h3 className="text-lg font-semibold text-textPrimary">Required Indicators</h3>
              {indicatorConfigs.map((indConfig, index) => {
                const indicatorMeta = indicatorMetadata.find(ind => ind.name === indConfig.name);
                if (!indicatorMeta) return null;

                return (
                  <div key={`${indConfig.name}-${index}`} className="space-y-2 border border-borderDefault rounded-md p-3 bg-backgroundOffset">
                    <h4 className="text-md font-semibold text-textPrimary">{indicatorMeta.display_name}</h4>
                    {indicatorMeta.parameters.map(indParamDef => {
                      const reqInd = selectedStrategy.required_indicators.find(ri => ri.name === indConfig.name);
                      const linkedStrategyParamName = reqInd?.params[indParamDef.name];
                      const isLinked = !!linkedStrategyParamName;

                      return (
                        <div key={indParamDef.name} className="space-y-1">
                          <label htmlFor={`${indConfig.name}-${indParamDef.name}`} className="block text-sm font-medium text-textPrimary">
                            {indParamDef.display_name}
                          </label>
                          <Input
                            type={indParamDef.parameter_type === 'int' || indParamDef.parameter_type === 'float' ? 'number' : 'text'}
                            id={`${indConfig.name}-${indParamDef.name}`}
                            value={indConfig.params[indParamDef.name] || ''}
                            onChange={(e) => {
                              if (!isLinked) {
                                const newIndicatorConfigs = [...indicatorConfigs];
                                newIndicatorConfigs[index].params[indParamDef.name] = e.target.value;
                                setIndicatorConfigs(newIndicatorConfigs);
                              }
                            }}
                            disabled={isLinked}
                            className="mt-1 w-full"
                          />
                          {indParamDef.description && (
                            <p className="text-xs text-textSecondary">{indParamDef.description}</p>
                          )}
                          {isLinked && (
                            <p className="text-xs text-textSecondary italic">
                              (Linked to strategy parameter: {selectedStrategy.parameters.find(p => p.name === linkedStrategyParamName)?.display_name || linkedStrategyParamName})
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Notes */}
      <div>
        <label htmlFor="notes" className="block text-sm font-medium text-textPrimary">
          Notes (Optional)
        </label>
        <textarea
          id="notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Any notes about this version..."
          className="mt-1 block w-full rounded-md border-borderDefault shadow-sm focus:border-primary focus:ring-primary sm:text-sm bg-backgroundSurface text-textPrimary p-2 h-24"
        />
      </div>

      <div className="flex justify-end space-x-3">
        <Button type="button" variant="secondary" onClick={() => navigate(`/bots/${botId}`)}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={isLoading || isLoadingMetadata || !selectedStrategy}>
          {isLoading ? 'Creating Version...' : 'Create Version'}
        </Button>
      </div>
    </form>
  );
};

export default BotVersionForm;
